/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Empty, Pagination, Select, Skeleton, Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ReviewCard } from './ReviewCard';
import type {
  BinaryReview,
  StarReview,
  ReviewConfig,
  ReviewServiceFunctions,
} from './types';

type ReviewListProps = {
  entityId: number;
  contextIds: Record<string, number | string>;
  config: ReviewConfig;
  getReviews: ReviewServiceFunctions['getReviews'];
  enableComments?: boolean;
  reviewService?: ReviewServiceFunctions;
  onCommentsChange?: () => void;
  isReadOnly?: boolean;
};

const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: 'desc(created_at)', labelId: 'review-sort-newest' },
  { value: 'asc(created_at)', labelId: 'review-sort-oldest' },
  { value: 'desc(binary_rating)', labelId: 'review-sort-highest' },
  { value: 'asc(binary_rating)', labelId: 'review-sort-lowest' },
];

const STAR_SORT_OPTIONS = [
  { value: 'desc(created_at)', labelId: 'review-sort-newest' },
  { value: 'asc(created_at)', labelId: 'review-sort-oldest' },
  { value: 'desc(star_rating)', labelId: 'review-sort-highest' },
  { value: 'asc(star_rating)', labelId: 'review-sort-lowest' },
];

export const ReviewList: React.FC<ReviewListProps> = ({
  entityId,
  contextIds,
  config,
  getReviews,
  enableComments = false,
  reviewService,
  onCommentsChange,
  isReadOnly = false,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('desc(created_at)');

  const isBinary = config.ratingType === 'binary';
  const sortOptions = isBinary ? SORT_OPTIONS : STAR_SORT_OPTIONS;

  const queryKey = [
    config.queryKeyPrefix,
    ...Object.values(contextIds),
    entityId,
    sortBy,
    currentPage,
  ];

  const { data: reviewsResponse, isLoading } = useQuery(
    queryKey,
    () => {
      const skip = (currentPage - 1) * PAGE_SIZE;

      return getReviews({
        entityId,
        skip,
        take: PAGE_SIZE,
        sort: sortBy,
      });
    },
    {
      enabled: !!entityId,
      keepPreviousData: true,
    }
  );

  const reviews = (reviewsResponse as any) || [];
  const totalCount = reviews.length;

  if (isLoading) {
    return (
      <div css={styles.container}>
        <div css={styles.loadingContainer}>
          <Skeleton active paragraph={{ rows: 3 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div css={styles.container}>
        <Empty
          image={<InboxOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description={
            <div>
              <Typography.Text strong css={styles.emptyTitle}>
                {intl.formatMessage({ id: 'review-no-reviews-yet' })}
              </Typography.Text>
              <Typography.Text type="secondary" css={styles.emptyDescription}>
                {intl.formatMessage({ id: 'review-no-reviews-description' })}
              </Typography.Text>
            </div>
          }
          css={styles.emptyState}
        />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.reviewsList}>
        {reviews.map((review: BinaryReview | StarReview) => (
          <ReviewCard
            key={review.id}
            review={review}
            phase={isBinary ? 'screening' : 'evaluation'}
            isCurrentUser={false}
            enableComments={enableComments}
            reviewService={reviewService}
            contextIds={contextIds}
            entityId={entityId}
            onCommentsChange={onCommentsChange}
            isReadOnly={isReadOnly}
          />
        ))}
      </div>

      {totalCount > PAGE_SIZE && (
        <div css={styles.paginationContainer}>
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={PAGE_SIZE}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} reviews`
            }
          />
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingBottom: 12,
    borderBottom: '1px solid #f0f0f0',
  },

  reviewsCount: {
    fontSize: 14,
  },

  sortSelect: {
    minWidth: 150,
  },

  reviewsList: {
    display: 'flex',
    flexDirection: 'column',
  },

  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 16,
  },

  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  emptyState: {
    padding: '40px 20px',
  },

  emptyTitle: {
    display: 'block',
    fontSize: 16,
    marginBottom: 4,
  },

  emptyDescription: {
    display: 'block',
    fontSize: 14,
  },
});
